/**
 * Reusable Instrument Selector Component
 * 
 * A modal-based instrument search and selection component that can be used across
 * multiple contexts (manual subscriptions, watchlist, strategies, etc.)
 * 
 * Features:
 * - Search by symbol OR company name
 * - Server-side filter by instrument type and exchange
 * - Display all matching instruments with details (including options: strike, type)
 * - Show linked broker sources for validation
 * - Selection callback for parent component
 * 
 * Usage:
 * ```jsx
 * const [open, setOpen] = useState(false);
 * const handleSelect = (instrument) => {
 *   console.log('Selected:', instrument);
 *   setOpen(false);
 * };
 * 
 * <InstrumentSelector
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   onSelect={handleSelect}
 *   title="Select Instrument for Manual Subscription" // optional
 * />
 * ```
 * 
 * @module components/common/InstrumentSelector
 */

import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { 
  FiSearch, FiX, FiCheck, FiCalendar, FiFilter, 
  FiChevronRight, FiDatabase, FiLayers, FiArrowLeft 
} from 'react-icons/fi';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useAuth } from '../../contexts/AuthContext';
import { services } from '../../services/api';

// Standard instrument types
const INSTRUMENT_TYPES = ['EQUITY', 'FUTURE', 'OPTION', 'INDEX', 'CURRENCY', 'COMMODITY'];

// Common exchanges
const EXCHANGES = ['NSE', 'BSE', 'NFO', 'BFO', 'MCX', 'CDS'];

const InstrumentSelector = ({ 
  open, 
  onClose, 
  onSelect, 
  title = "Select Instrument",
  selectedInstrument = null,
  requireBrokerSelection = false, // NEW: If true, user must select specific broker source
  userBrokerIds = [] // NEW: List of broker IDs user has configured (filter sources)
}) => {
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Admin Override
  const [includeAll, setIncludeAll] = useState(false);
  const isAdmin = user?.is_superuser || user?.role === 'admin' || user?.role?.toLowerCase() === 'admin';
  
  // Server-side filters (applied during search)
  const [searchInstrumentType, setSearchInstrumentType] = useState('');
  const [searchExchange, setSearchExchange] = useState('');
  
  // Search results with pagination
  const [searchResults, setSearchResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const RESULTS_PER_PAGE = 500; // Increased to load more results with filters
  
  // Client-side filters (applied after search on loaded results)
  const [filterExpiry, setFilterExpiry] = useState('');
  
  // Selected instrument for viewing broker sources
  const [viewingInstrument, setViewingInstrument] = useState(null);
  const [brokerSources, setBrokerSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [selectedBrokerSource, setSelectedBrokerSource] = useState(null);

  // Get unique expiry dates for filter dropdown (from loaded results)
  const uniqueExpiries = useMemo(() => {
    const expiries = new Set();
    searchResults.forEach(inst => {
      if (inst.expiry_date) expiries.add(inst.expiry_date);
    });
    return Array.from(expiries).sort((a, b) => new Date(a) - new Date(b));
  }, [searchResults]);

  // Filtered results based on client-side expiry filter
  const filteredResults = useMemo(() => {
    let results = searchResults;
    
    if (filterExpiry) {
      results = results.filter(inst => inst.expiry_date === filterExpiry);
    }
    
    return results;
  }, [searchResults, filterExpiry]);

  // SEARCH EFFECT: Auto-load instruments when selector opens (Browse Mode)
  useEffect(() => {
    if (open) {
       // Fetch initial list (Browse Mode)
       searchInstruments(1, false);
    }
  }, [open]);

  // Search instruments by symbol or name with server-side filters
  const searchInstruments = async (page = 1, append = false) => {
    // Allow empty search to browse all instruments
    // if (!searchQuery.trim()) {
    //   showSnackbar('Please enter a search query', 'warning');
    //   return;
    // }

    setLoading(true);
    if (!append) {
      setViewingInstrument(null);
      setBrokerSources([]);
      setFilterExpiry('');
      setSearchResults([]);
      setIncludeAll(false); // Reset include all on new fresh search unless explicitly unchecked?
      // actually, let's keep the checkbox state if user set it. 
      // But verify if we want to reset it on close? Yes, done in handleClose.
    }
    
    try {
      // Build search params with server-side filters
      const searchParams = {
        symbol: searchQuery.trim(),
        page: page,
        limit: RESULTS_PER_PAGE,
        include_all: includeAll // Send admin override flag
      };
      
      // Add server-side filters if set
      if (searchInstrumentType) {
        searchParams.instrument_type = searchInstrumentType;
      }
      if (searchExchange) {
        searchParams.exchange = searchExchange;
      }
      
      const response = await services.instrumentMaster.search(searchParams);

      // Backend returns: { total, page, page_size, instruments: [...] }
      const results = response.data?.instruments || [];
      const total = response.data?.total || 0;
      
      if (append) {
        setSearchResults(prev => [...prev, ...results]);
      } else {
        setSearchResults(results);
      }
      
      setTotalResults(total);
      setCurrentPage(page);
      setHasMore(results.length === RESULTS_PER_PAGE && (page * RESULTS_PER_PAGE) < total);

      if (!append) {
        if (results.length === 0) {
          showSnackbar('No instruments found', 'info');
        } else {
          showSnackbar(`Found ${total} instrument(s), showing ${results.length}`, 'success');
        }
      }
    } catch (error) {
      console.error('Error searching instruments:', error);
      showSnackbar(error.response?.data?.error?.message || 'Failed to search instruments', 'error');
      if (!append) {
        setSearchResults([]);
        setTotalResults(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load more results
  const loadMoreResults = () => {
    searchInstruments(currentPage + 1, true);
  };

  // View broker sources for validation
  const viewBrokerSources = async (instrument) => {
    setViewingInstrument(instrument);
    setLoadingSources(true);
    setSelectedBrokerSource(null); // Reset broker selection
    
    try {
      const response = await services.instrumentMaster.getMappings(instrument.instrument_id);
      // Backend returns array directly: [{ broker_id, broker_symbol, ... }, ...]
      let mappings = response.data || [];
      
      // Filter by user's configured broker accounts if provided
      // Note: userBrokerIds can be broker names (strings) or broker IDs (numbers)
      if (userBrokerIds && userBrokerIds.length > 0) {
        mappings = mappings.filter(m => 
          userBrokerIds.includes(m.broker_id) || 
          userBrokerIds.includes(m.broker_name) ||
          userBrokerIds.includes(String(m.broker_id))
        );
      }
      
      setBrokerSources(mappings);
      
      if (mappings.length === 0 && userBrokerIds && userBrokerIds.length > 0) {
        showSnackbar('No broker sources found for your configured brokers', 'warning');
      }
    } catch (error) {
      console.error('Error fetching broker sources:', error);
      showSnackbar('Failed to load broker sources', 'error');
      setBrokerSources([]);
    } finally {
      setLoadingSources(false);
    }
  };

  // Handle instrument selection
  const handleSelect = (instrument, brokerSource = null) => {
    // If broker selection is required and no broker source provided, show error
    if (requireBrokerSelection && !brokerSource) {
      showSnackbar('Please select a specific broker source', 'warning');
      return;
    }
    
    // Prepare selection data
    const selectionData = {
      instrument,
      brokerSource: brokerSource || null // Include broker source if provided
    };
    
    onSelect(selectionData);
    
    // Reset state
    setSearchQuery('');
    setSearchResults([]);
    setViewingInstrument(null);
    setBrokerSources([]);
    setSelectedBrokerSource(null);
    setSearchInstrumentType('');
    setSearchInstrumentType('');
    setSearchExchange('');
    setFilterExpiry('');
    setIncludeAll(false);
  };

  // Clear client-side expiry filter
  const clearFilters = () => {
    setFilterExpiry('');
  };

  // Format expiry date
  const formatExpiry = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Get broker source count from instrument data
  const getSourceCount = (instrument) => {
    return instrument.broker_data_sources?.broker_ids?.length || 0;
  };

  // Close and reset
  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setViewingInstrument(null);
    setBrokerSources([]);
    setSearchInstrumentType('');
    setSearchExchange('');
    setFilterExpiry('');
    setIncludeAll(false);
    onClose();
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-bg-1 rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--divider)] bg-bg-3 flex items-center justify-between">
          <h2 className="ds-h2 text-fg-1 flex items-center gap-2">
            <FiSearch className="text-blue-600" />
            {title}
          </h2>
          <button
            onClick={handleClose}
            className="text-fg-2 hover:text-fg-2 dark:hover:text-fg-3 transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Search Section with Server-Side Filters */}
          <div className="bg-bg-3/50 p-4 rounded-lg mb-6">
            <div className="flex flex-wrap gap-4">
              {/* Search Input */}
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-fg-2 mb-2">
                  Search by Symbol or Company Name
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      // If there is exactly one result, select it immediately
                      if (searchResults.length === 1) {
                        handleSelect(searchResults[0]);
                      } else {
                        searchInstruments();
                      }
                    }
                  }}
                  placeholder="e.g., RELIANCE, NIFTY (or leave empty to browse)"
                  className="ds-input w-full"
                  autoFocus
                />
              </div>
              
              {/* Type Filter (Server-Side) */}
              <div className="w-40">
                <label className="block text-sm font-medium text-fg-2 mb-2">
                  Type
                </label>
                <select
                  value={searchInstrumentType}
                  onChange={(e) => setSearchInstrumentType(e.target.value)}
                  className="ds-input w-full"
                >
                  <option value="">All Types</option>
                  {INSTRUMENT_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              {/* Exchange Filter (Server-Side) */}
              <div className="w-32">
                <label className="block text-sm font-medium text-fg-2 mb-2">
                  Exchange
                </label>
                <select
                  value={searchExchange}
                  onChange={(e) => setSearchExchange(e.target.value)}
                  className="ds-input w-full"
                >
                  <option value="">All</option>
                  {EXCHANGES.map(ex => (
                    <option key={ex} value={ex}>{ex}</option>
                  ))}
                </select>
              </div>
              
              {/* Show All Checkbox (Admin Only) */}
              {isAdmin && (
                <div className="flex items-center pt-6 px-2">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input 
                      type="checkbox"
                      checked={includeAll}
                      onChange={(e) => setIncludeAll(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-[var(--divider)] focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-fg-2">
                      Show All (Admin)
                    </span>
                  </label>
                </div>
              )}
              
              {/* Search Button */}
              <div className="flex items-end">
                <button
                  onClick={() => searchInstruments()}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <FiSearch />
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
          </div>

          {/* Viewing Broker Sources */}
          {viewingInstrument && (
            <div className="mb-6 space-y-4">
              {/* Instrument Header - Enhanced with Option Details */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => setViewingInstrument(null)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 flex items-center gap-1 text-sm"
                      >
                        <FiArrowLeft size={16} /> Back to results
                      </button>
                    </div>
                    <h3 className="ds-h3 text-fg-1">
                      {viewingInstrument.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="font-semibold text-fg-2">
                        {viewingInstrument.symbol}
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                        {viewingInstrument.exchange}
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                        {viewingInstrument.instrument_type}
                      </span>
                      {viewingInstrument.expiry_date && (
                        <span className="px-2 py-0.5 text-xs rounded bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 flex items-center gap-1">
                          <FiCalendar size={10} />
                          {formatExpiry(viewingInstrument.expiry_date)}
                        </span>
                      )}
                      {/* Strike Price for Options */}
                      {viewingInstrument.strike_price && (
                        <span className="px-2 py-0.5 text-xs rounded bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300 font-mono">
                          Strike: {viewingInstrument.strike_price}
                        </span>
                      )}
                      {/* Option Type (CE/PE) */}
                      {viewingInstrument.option_type && (
                        <span className={`px-2 py-0.5 text-xs rounded font-bold ${
                          viewingInstrument.option_type === 'CE' 
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                            : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        }`}>
                          {viewingInstrument.option_type === 'CE' ? 'CALL' : 'PUT'}
                        </span>
                      )}
                    </div>
                  </div>
                  {!requireBrokerSelection && (
                    <button
                      onClick={() => handleSelect(viewingInstrument)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors flex items-center gap-2"
                    >
                      <FiCheck />
                      Select Instrument
                    </button>
                  )}
                </div>
              </div>

              {/* Broker Sources */}
              <div className="bg-white dark:bg-bg-1 rounded-lg border border-[var(--divider)]">
                <div className="px-4 py-3 border-b border-[var(--divider)] bg-bg-3">
                  <h4 className="text-md font-semibold text-fg-1 flex items-center gap-2">
                    <FiLayers className="text-indigo-600" />
                    Broker Sources ({brokerSources.length})
                    {requireBrokerSelection && (
                      <span className="ds-badge sm warn ml-2">
                        Selection Required
                      </span>
                    )}
                  </h4>
                  <p className="text-sm text-fg-2 mt-1">
                    {requireBrokerSelection 
                      ? 'Select a specific broker source to continue' 
                      : 'Validate instrument data from different brokers before selection'}
                  </p>
                </div>

                {loadingSources ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                  </div>
                ) : brokerSources.length === 0 ? (
                  <div className="text-center py-12 text-fg-2">
                    <FiDatabase className="mx-auto mb-3 text-fg-3" size={48} />
                    <p>No broker sources found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-64 overflow-y-auto">
                    {brokerSources.map((source, idx) => {
                      const isSelected = selectedBrokerSource?.broker_id === source.broker_id;
                      return (
                      <div 
                        key={idx} 
                        className={`p-4 transition-colors ${
                          requireBrokerSelection 
                            ? isSelected 
                              ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500' 
                              : 'hover:bg-bg-3 cursor-pointer'
                            : ''
                        }`}
                        onClick={() => {
                          if (requireBrokerSelection) {
                            setSelectedBrokerSource(source);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {requireBrokerSelection && isSelected && (
                                <FiCheck className="text-green-600 dark:text-green-400" size={20} />
                              )}
                              <span className="font-semibold text-fg-1">
                                {source.broker_name || `Broker ${idx + 1}`}
                              </span>
                              {source.is_primary && (
                                <span className="ds-badge sm profit">
                                  <FiCheck size={10} /> Primary
                                </span>
                              )}
                              {source.is_active ? (
                                <span className="ds-badge sm profit">
                                  Active
                                </span>
                              ) : (
                                <span className="ds-badge sm neutral">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-fg-2">Broker Symbol:</span>
                                <span className="ml-2 font-mono text-fg-1">
                                  {source.broker_symbol}
                                </span>
                              </div>
                              <div>
                                <span className="text-fg-2">Broker Token:</span>
                                <span className="ml-2 font-mono text-fg-1">
                                  {source.broker_token || 'N/A'}
                                </span>
                              </div>
                              {source.updated_at && (
                                <div className="col-span-2">
                                  <span className="text-fg-2">Last Updated:</span>
                                  <span className="ml-2 text-fg-2">
                                    {new Date(source.updated_at).toLocaleString()}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          {requireBrokerSelection && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBrokerSource(source);
                              }}
                              className={`ml-4 px-3 py-1.5 text-sm rounded-md transition-colors ${
                                isSelected
                                  ? 'bg-green-600 text-white'
                                  : 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                              }`}
                            >
                              {isSelected ? '✓ Selected' : 'Select'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                    })}
                  </div>
                )}
              </div>
              
              {/* Confirm Selection Button (when broker selection required) */}
              {requireBrokerSelection && selectedBrokerSource && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-fg-1">
                        ✓ Broker Source Selected
                      </p>
                      <p className="text-xs text-fg-2 mt-1">
                        {selectedBrokerSource.broker_name}: {selectedBrokerSource.broker_symbol}
                      </p>
                    </div>
                    <button
                      onClick={() => handleSelect(viewingInstrument, selectedBrokerSource)}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors flex items-center gap-2"
                    >
                      <FiCheck />
                      Confirm Selection
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && !viewingInstrument && (
            <div className="bg-white dark:bg-bg-1 rounded-lg border border-[var(--divider)]">
              {/* Results Header */}
              <div className="px-4 py-3 border-b border-[var(--divider)] bg-bg-3">
                <h3 className="ds-h3 text-fg-1">
                  Search Results ({totalResults} total, showing {filteredResults.length})
                </h3>
                <p className="text-sm text-fg-2 mt-1">
                  Click on an instrument to view broker sources and select
                </p>
              </div>

              {/* Client-Side Filters (Expiry only - Type/Exchange are server-side now) */}
              {uniqueExpiries.length > 0 && (
                <div className="px-4 py-3 border-b border-[var(--divider)] bg-bg-3/50 dark:bg-bg-2/50">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <FiFilter className="text-fg-2" size={16} />
                      <span className="text-sm font-medium text-fg-2">Filter loaded results:</span>
                    </div>
                    
                    {/* Expiry Filter (Client-Side) */}
                    <div className="flex items-center gap-2">
                      <FiCalendar className="text-fg-2" size={14} />
                      <label className="text-sm text-fg-2">Expiry:</label>
                      <select
                        value={filterExpiry}
                        onChange={(e) => setFilterExpiry(e.target.value)}
                        className="ds-input"
                      >
                        <option value="">All Expiries ({uniqueExpiries.length})</option>
                        {uniqueExpiries.map(exp => (
                          <option key={exp} value={exp}>{formatExpiry(exp)}</option>
                        ))}
                      </select>
                    </div>

                    {/* Clear Filters */}
                    {filterExpiry && (
                      <button
                        onClick={clearFilters}
                        className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md flex items-center gap-1"
                      >
                        <FiX size={14} />
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Results List */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[400px] overflow-y-auto">
                {filteredResults.length === 0 ? (
                  <div className="px-4 py-8 text-center text-fg-2">
                    No instruments match the selected filters.
                  </div>
                ) : (
                  filteredResults.map((instrument) => (
                    <div
                      key={instrument.instrument_id}
                      className="p-4 hover:bg-bg-3 transition-colors"
                      onDoubleClick={() => handleSelect(instrument)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSelect(instrument); }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className="font-semibold text-fg-1">
                              {instrument.symbol}
                            </span>
                            <span className="px-2 py-0.5 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                              {instrument.exchange}
                            </span>
                            <span className="px-2 py-0.5 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                              {instrument.instrument_type}
                            </span>
                            {instrument.expiry_date && (
                              <span className="px-2 py-0.5 text-xs rounded bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 flex items-center gap-1">
                                <FiCalendar size={10} />
                                {formatExpiry(instrument.expiry_date)}
                              </span>
                            )}
                            {/* Strike Price */}
                            {instrument.strike_price && (
                              <span className="px-2 py-0.5 text-xs rounded bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300 font-mono">
                                ₹{instrument.strike_price}
                              </span>
                            )}
                            {/* Option Type (CE/PE) */}
                            {instrument.option_type && (
                              <span className={`px-2 py-0.5 text-xs rounded font-bold ${
                                instrument.option_type === 'CE' 
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                              }`}>
                                {instrument.option_type}
                              </span>
                            )}
                            {/* Source Count Badge */}
                            {getSourceCount(instrument) > 0 && (
                              <span className="px-2 py-0.5 text-xs rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 flex items-center gap-1">
                                <FiDatabase size={10} />
                                {getSourceCount(instrument)} sources
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-fg-2 mb-1">
                            {instrument.name}
                          </div>
                          <div className="text-xs text-fg-3 font-mono">
                            ISIN: {instrument.isin || 'N/A'}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => viewBrokerSources(instrument)}
                            className="px-3 py-1.5 text-sm bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-md flex items-center gap-1 transition-colors"
                          >
                            <FiDatabase size={14} />
                            View Sources
                          </button>
                          <button
                            onClick={() => handleSelect(instrument)}
                            className="px-3 py-1.5 text-sm bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/40 text-green-700 dark:text-green-300 rounded-md flex items-center gap-1 transition-colors"
                          >
                            <FiCheck size={14} />
                            Select
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Load More Button */}
              {hasMore && !viewingInstrument && filteredResults.length > 0 && (
                <div className="px-4 py-3 border-t border-[var(--divider)] bg-bg-3/50 dark:bg-bg-2/50">
                  <button
                    onClick={loadMoreResults}
                    disabled={loading}
                    className="w-full px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Loading...
                      </>
                    ) : (
                      <>
                        <FiChevronRight className="rotate-90" />
                        Load More ({searchResults.length} of {totalResults})
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {searchResults.length === 0 && !loading && !viewingInstrument && (
            <div className="text-center py-16 text-fg-2">
              <FiSearch className="mx-auto mb-4 text-fg-3" size={64} />
              <h3 className="ds-h3 text-fg-2">Search for Instruments</h3>
              <p className="mt-2 text-fg-2 max-w-md mx-auto">
                Enter a symbol (like RELIANCE, NIFTY) or company name, 
                select Type and Exchange to narrow results.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--divider)] bg-bg-3 flex justify-end">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-fg-2 hover:bg-bg-3 rounded-md transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InstrumentSelector;
